import React, { useEffect } from 'react'
import { FragmentType, graphql, useFragment } from '../../../gql'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import tinycolor from 'tinycolor2'
import { faLayerGroup, faHashtag, faBox, faUser } from '@fortawesome/pro-duotone-svg-icons'
import { faInboxIn, faTag } from '@fortawesome/pro-solid-svg-icons'
import { Tag } from '../../../components/Tag'
import { areAllOrdersHasSameStatus, formatDate, matchCountWithWord, PROCESS_STATUS_NAME } from '../../../lib/Util'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/shadcn/tooltip'

export const GroupListItem_OrderGroupFragment = graphql(`
  fragment GroupListItem_OrderGroup on OrderGroup {
    id
    name
    created
    ordersCount
    tags {
      id
      name
      color
    }
    user {
      id
      name
    }
    tags {
      ...TagComponent_TagFragment
    }
  }
`)

type Props = {
  group: FragmentType<typeof GroupListItem_OrderGroupFragment>
  isUpdating?: boolean
}

export const GroupListItem: React.FC<Props> = ({ group: groupProps, isUpdating = false }) => {
  const group = useFragment(GroupListItem_OrderGroupFragment, groupProps)

  // Use a default color scheme similar to Order component
  const color = tinycolor.mix(tinycolor('rgb(100, 150, 200)'), '#fff', 80).toString()

  const groupDetailItems = [
    {
      label: 'Počet objednávek',
      disabledLabel: 'Skupina neobsahuje žádné objednávky',
      icon: faBox,
      value: `${group.ordersCount || 0} ${matchCountWithWord(['objednávka', 'objednávky', 'objednávek'], group.ordersCount || 0)}`,
      isVisible: group.ordersCount > 0,
    },
    {
      label: 'Datum vytvoření',
      icon: faInboxIn,
      value: `${formatDate(group.created, true)}`,
      isVisible: !!group.created,
    },
  ]

  const renderGroupDetailItems = () => {
    let iconColor = tinycolor.mix(tinycolor('rgb(100, 150, 200)'), '#fff', 60).toString()

    // Ensure good contrast
    for (let i = 0; i < 100; i++) {
      if (tinycolor.readability(iconColor, color) > 1.6) {
        break
      }
      iconColor = tinycolor(iconColor).darken(1).toString()
    }

    return (
      <div className="p-3">
        {groupDetailItems.map((el, i) => (
          <Tooltip key={el.label}>
            <TooltipTrigger asChild>
              <div className={'flex flex-row items-center gap-3'}>
                <div className={'flex w-7 h-7 items-center justify-center'}>
                  <FontAwesomeIcon icon={el.icon} color={iconColor} size={'lg'} />
                </div>
                <span className={'font-semibold'} style={!el.isVisible ? { color: 'rgba(0, 0, 0, 0.3)' } : {}}>
                  {el.isVisible ? el.value : el.disabledLabel || 'Nezadano'}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>{el.label}</TooltipContent>
          </Tooltip>
        ))}
        {group?.tags && group?.tags?.length > 0 && (
          <div className="flex flex-row flex-wrap items-center gap-2 mt-2">
            <div className={'flex items-center gap-2 flex-wrap'}>
              {(group?.tags || []).map(tag => (
                <div key={tag.id}>
                  <Tag tag={tag} size="compact" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const LinkElement = isUpdating ? ({ children, ...props }: any) => <div {...props}>{children}</div> : Link

  return (
    <LinkElement
      to={`/group/${group?.id}`}
      className={`flex rounded-lg flex-1 flex-col shadow-md ${isUpdating ? 'opacity-50' : ''}`}
      style={{ backgroundColor: color, breakInside: 'avoid' }}>
      <div className={'flex flex-row gap-3 items-center justify-between px-3 py-2 rounded-t-lg bg-gray-50 orderShadow'}>
        <div className={'flex flex-row gap-3 items-center'}>
          <FontAwesomeIcon
            icon={faHashtag}
            size="lg"
            style={
              {
                '--fa-primary-color': 'rgb(100, 150, 200)',
                '--fa-secondary-color': 'rgb(60, 100, 140)',
                '--fa-secondary-opacity': 0.5,
              } as React.CSSProperties
            }
          />
          <span className={'font-bold text-lg'}>{group?.name}</span>
        </div>
        <div className={'flex flex-row gap-3 items-center'}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <FontAwesomeIcon
                  icon={faLayerGroup}
                  size="lg"
                  style={
                    {
                      '--fa-primary-color': 'rgb(100, 150, 200)',
                      '--fa-secondary-color': 'rgb(60, 100, 140)',
                      '--fa-secondary-opacity': 0.5,
                    } as React.CSSProperties
                  }
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>Skupina objednávek</TooltipContent>
          </Tooltip>
        </div>
      </div>
      {renderGroupDetailItems()}
    </LinkElement>
  )
}
